import {DataFetcher, PriorityConfig} from '@aboutyou/data-fetcher';
import {TreeNavigationItem} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tree_navigation_item.types';
import {originClick} from '@aboutyou/legacy-tracking-sdk';
import {usePathname} from '@aboutyou/router';
import React from 'react';
import {
  ExtendedTreeItem,
  NavigationTree,
  NavigationTreeProps,
} from '../../../features/NavigationTree/NavigationTree';
import {NavigationTreeLoading} from '../../../features/NavigationTree/NavigationTreeLoading';
import {useAppliedPotentialFilters} from '../../../features/ProductStream/utils';
import {FIRST_PRODUCT_ID_PARAM} from '../../../features/firstProductId/useFirstProductIds';
import {useMergedPath} from '../../../shared-deprecated/useMergedPath';
import {useSearchCategoryTreeSection} from '../hooks/useSearchPageBulkSection';

export const SearchNavigationTree: React.FC<
  PriorityConfig & {
    className?: string;
  }
> = ({renderingPriority, ...otherProps}) => {
  return (
    <DataFetcher
      request={useSearchCategoryTreeSection()}
      Error={null}
      Loading={NavigationTreeLoading}
      View={SearchNavigationTreeView}
      viewProps={{
        ...otherProps,
        trackOriginKey: 'category_tree_side' as originClick,
        testId: 'searchNavigationTree',
        excludeUrlParams: [
          FIRST_PRODUCT_ID_PARAM,
        ] as NavigationTreeProps['excludeUrlParams'],
      }}
      renderingPriority={renderingPriority}
    />
  );
};

const SearchNavigationTreeView: React.FC<NavigationTreeProps> = ({
  categoryTree,
  ...otherProps
}) => {
  const normalizeNodes = useNormalizeNodes();
  const hasAppliedFilters = Boolean(
    Object.keys(useAppliedPotentialFilters()).length,
  );

  return (
    <NavigationTree
      categoryTree={normalizeNodes(categoryTree)}
      useButtonLink={hasAppliedFilters}
      hideAllDealItems
      {...otherProps}
    />
  );
};

const useNormalizeNodes = () => {
  const getMergedPath = useMergedPath();
  const pathname = usePathname();

  // Helper to check if any root node in the tree is active
  const hasActiveNode = (nodes: TreeNavigationItem[]): boolean => {
    return nodes.some(node => node.active);
  };

  const normalizeNode = (
    node: TreeNavigationItem,
    index: number,
    isFirstLevel: boolean,
    shouldActivateFirst: boolean,
  ): ExtendedTreeItem => {
    const isDisabled = !node.productCount;

    // For disabled categories, use current URL to prevent navigation
    const currentUrl = getMergedPath({
      path: pathname,
    });

    return {
      ...node,
      active:
        node.active || (isFirstLevel && index === 0 && shouldActivateFirst),
      category: {
        ...node.category,
        // For disabled categories, set path to current URL so clicking doesn't navigate
        path: isDisabled ? currentUrl : node.category.path,
        // Use useMergedPath to merge category=id into current URL, preserving other params like term and filters
        // Don't set targetPath for disabled categories to prevent navigation
        targetPath: isDisabled
          ? undefined
          : getMergedPath({
              path: `${pathname}?category=${node.category.id}`,
              config: {excludeUrlParams: new Set(['category', 'path'])},
            }),
      },
      children: node.children.map((child, i) =>
        normalizeNode(child, i, false, false),
      ),
    };
  };

  return (nodes: TreeNavigationItem[]) => {
    const shouldActivateFirst = !hasActiveNode(nodes);
    return nodes.map((node, index) =>
      normalizeNode(node, index, true, shouldActivateFirst),
    );
  };
};
